import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Send, Trash2, Plus, CheckCircle2, AlertCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api, API, errMsg } from "../lib/api";
import { PageHeader } from "../components/app/PageHeader";

const inline = (t) => t.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).map((s, i) =>
  s.startsWith("**") ? <b key={i}>{s.slice(2, -2)}</b> : s.startsWith("`") ? <code key={i} className="rounded bg-[#F5F0E6] px-1 text-xs">{s.slice(1, -1)}</code>
    : s.length > 2 && s.startsWith("*") && s.endsWith("*") ? <i key={i}>{s.slice(1, -1)}</i> : s);
const lineInline = (l) => (/^_.+_$/.test(l.trim()) ? <i>{l.trim().slice(1, -1)}</i> : inline(l));

const cells = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
const MdTable = ({ rows }) => {
  const body = rows.filter((r) => !/^\s*\|?\s*:?-{2,}/.test(r));
  const [head, ...rest] = body.map(cells);
  return (
    <div className="my-2 overflow-x-auto"><table className="w-full text-xs"><thead><tr>{head.map((c, i) => <th key={i} className="border-b border-amber-700/20 bg-[#F5F0E6] px-2 py-1 text-left">{inline(c)}</th>)}</tr></thead>
      <tbody>{rest.map((r, i) => <tr key={i} className="border-b border-amber-700/10">{r.map((c, j) => <td key={j} className="px-2 py-1">{inline(c)}</td>)}</tr>)}</tbody></table></div>
  );
};

const blocks = (text) => text.split("\n").reduce((acc, l) => {
  const last = acc[acc.length - 1];
  if (l.trim().startsWith("|")) {
    if (last?.table) last.rows.push(l); else acc.push({ table: true, rows: [l] });
  } else acc.push({ line: l });
  return acc;
}, []);

export const Md = ({ text }) => (
  <div className="space-y-1.5 text-sm leading-relaxed text-stone-800">
    {blocks(text).map((b, i) => {
      if (b.table) return <MdTable key={i} rows={b.rows} />;
      const l = b.line.replace(/^\s*>\s?/, "");
      if (/^\s*(-{3,}|\*{3,})\s*$/.test(l)) return <hr key={i} className="border-amber-700/15" />;
      if (l.startsWith("# ")) return <h2 key={i} className="pt-2 font-serif-display text-xl font-bold text-amber-950">{l.slice(2)}</h2>;
      if (l.startsWith("## ")) return <h3 key={i} className="pt-3 font-serif-display text-lg font-semibold text-amber-900">{l.slice(3)}</h3>;
      if (l.startsWith("### ")) return <h4 key={i} className="pt-2 font-semibold text-stone-800">{l.slice(4)}</h4>;
      if (/^\s*[-*] /.test(l)) return <p key={i} className="pl-4 -indent-3">• {inline(l.replace(/^\s*[-*] /, ""))}</p>;
      if (/^\s*\d+\. /.test(l)) return <p key={i} className="pl-4">{inline(l.trim())}</p>;
      return l.trim() ? <p key={i}>{lineInline(l)}</p> : null;
    })}
  </div>
);

const Review = ({ m, onChange }) => {
  const st = m.review?.status || "unreviewed";
  const set = async (status) => { try { await api.patch(`/analyst/messages/${m.message_id}/review`, { status }); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-amber-700/10 pt-3 text-xs" data-testid={`review-${m.message_id}`}>
      {st === "unreviewed" && <span className="flex items-center gap-1 text-amber-800"><ShieldAlert className="h-3 w-3" />Not yet reviewed by a pastoral leader</span>}
      {st === "reviewed" && <span className="flex items-center gap-1 text-[#285E3B]"><CheckCircle2 className="h-3 w-3" />Reviewed by {m.review.by}</span>}
      {st === "disputed" && <span className="flex items-center gap-1 text-red-700"><AlertCircle className="h-3 w-3" />Marked as not reliable by {m.review.by}</span>}
      {st !== "reviewed" && <Button size="sm" variant="outline" className="h-7" onClick={() => set("reviewed")} data-testid={`mark-reviewed-${m.message_id}`}>Mark reviewed</Button>}
      {st !== "disputed" && <Button size="sm" variant="ghost" className="h-7" onClick={() => set("disputed")} data-testid={`mark-disputed-${m.message_id}`}>Mark not reliable</Button>}
    </div>
  );
};

const Message = ({ m, onChange }) => m.role === "user" ? (
  <div className="ml-auto max-w-2xl rounded-xl bg-amber-900 px-4 py-3 text-sm text-amber-50" data-testid="analyst-user-message">{m.content}</div>
) : (
  <div className="max-w-3xl rounded-xl border border-amber-700/15 bg-white p-5" data-testid="analyst-answer">
    <p className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-amber-800/80"><Bot className="h-3 w-3" />Mabuhay Pastoral Analyst · AI draft{m.context_generated_on ? ` · data as of ${m.context_generated_on}` : ""}</p>
    <Md text={m.content} />
    {m.message_id && <Review m={m} onChange={onChange} />}
  </div>
);

async function streamAsk(body, onEvent) {
  const res = await fetch(`${API}/analyst/ask`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "The Analyst could not answer");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => { if (p.startsWith("data: ")) onEvent(JSON.parse(p.slice(6))); });
  }
}

export default function Analyst() {
  const [threads, setThreads] = useState([]);
  const [tid, setTid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [examples, setExamples] = useState([]);
  const end = useRef(null);
  const loadThreads = () => api.get("/analyst/threads").then((r) => setThreads(r.data));
  const open = (id) => api.get(`/analyst/threads/${id}`).then((r) => { setTid(id); setMsgs(r.data.messages); });
  useEffect(() => { loadThreads(); api.get("/analyst/examples").then((r) => setExamples(r.data)); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (text) => {
    const question = (text || q).trim();
    if (question.length < 3 || busy) return;
    setQ(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    let thread = tid;
    try {
      await streamAsk({ question, thread_id: tid }, (ev) => {
        if (ev.type === "meta") { thread = ev.thread_id; setTid(ev.thread_id); }
        if (ev.type === "delta") setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], content: m[m.length - 1].content + ev.text }]);
        if (ev.type === "error") toast.error(ev.message);
      });
      await open(thread); loadThreads();
    } catch (e) { toast.error(e.message); setMsgs((m) => m.slice(0, -1)); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/analyst/threads/${id}`); if (id === tid) { setTid(null); setMsgs([]); } loadThreads(); };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Phase 4 · AI assistance" title="Mabuhay Pastoral Analyst" testId="analyst-header">
        Ask questions about verified parish data. Answers separate findings from suggestions, show their evidence and name what is missing. They are drafts: always review before acting. Never enter names or private details here.
      </PageHeader>
      <Link to="/analyst/drafts"><Button variant="outline" data-testid="open-ppc-drafts-button">Draft the monthly PPC summary</Button></Link>
      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="space-y-2" data-testid="analyst-threads">
          <Button variant="outline" className="w-full justify-start" onClick={() => { setTid(null); setMsgs([]); }} data-testid="new-conversation-button"><Plus className="h-4 w-4 mr-2" />New conversation</Button>
          {threads.map((t) => (
            <div key={t.thread_id} className={`group flex items-center gap-1 rounded-lg px-3 py-2 text-sm ${t.thread_id === tid ? "bg-amber-100" : "hover:bg-[#F5F0E6]"}`}>
              <button className="flex-1 truncate text-left" onClick={() => open(t.thread_id)} data-testid={`thread-${t.thread_id}`}>{t.title}</button>
              <button onClick={() => remove(t.thread_id)} aria-label="Delete" className="opacity-50 hover:opacity-100" data-testid={`delete-thread-${t.thread_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </aside>
        <section className="min-w-0 space-y-4">
          {!msgs.length && (
            <div className="grid gap-2 sm:grid-cols-2" data-testid="analyst-examples">
              {examples.map((e, i) => <button key={e} onClick={() => ask(e)} className="rounded-xl border border-amber-700/15 bg-white p-4 text-left text-sm text-stone-700 hover:border-amber-700/40 transition-colors" data-testid={`example-${i}`}>{e}</button>)}
            </div>
          )}
          <div className="space-y-4" data-testid="analyst-messages">
            {msgs.map((m, i) => <Message key={m.message_id || i} m={m} onChange={() => open(tid)} />)}
            {busy && msgs[msgs.length - 1]?.content === "" && <p className="text-sm italic text-stone-500" data-testid="analyst-thinking">Reading the parish data…</p>}
            <div ref={end} />
          </div>
          <div className="sticky bottom-4 flex gap-2 rounded-xl border border-amber-700/20 bg-white p-3 shadow-lg">
            <Textarea rows={2} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="Ask about survey findings, priorities, programs or results…" className="resize-none border-0 focus-visible:ring-0" data-testid="analyst-question-input" />
            <Button onClick={() => ask()} disabled={busy || q.trim().length < 3} className="self-end bg-amber-900 hover:bg-[#5B210B]" data-testid="analyst-send-button"><Send className="h-4 w-4" /></Button>
          </div>
        </section>
      </div>
    </div>
  );
}
